import React, { useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Select, Input, Button } from "antd";
import moment from "moment";

import ExamCard from "../../Exams/ExamCard.component";
import useDebounce from "../../Common/hooks/useDebounce";
import { instance } from "../../../constants/constString";

const { Option } = Select;
const sharedInputStyles = {
  padding: "7px 11px",
  fontSize: "16px",
  borderColor: "gray",
  borderRadius: "10px",
  boxSizing: "border-box", // Ensures padding is included in the total width/height
  lineHeight: "1.5715", // Match the line-height too for vertical centering
};
const LiveExamTab = (props) => {
  const { allSubjects, examList, liveExams, selectedSubject, courseId } = props;
  const inputRef = useRef();
  const [_examList, _setExamList] = useState(liveExams);
  const [searchText, setSearchText] = useState("");
  const [thePrompt, setThePrompt] = useState(""); // this will show up in search box
  const debouncedSearchTerm = useDebounce(searchText, 500); // 500ms delay

  const fetchPrompt = async () => {
    try {
      const response = await instance.get("/exam/getExamNamePrompt", {
        params: {
          search: searchText,
          courseId: courseId,
          isPracticeExam: false,
          subjectId: selectedSubject,
        },
      });
      setThePrompt(response?.data?.data ?? "");
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  useEffect(() => {
    if (debouncedSearchTerm?.length > 1) fetchPrompt();
    // eslint-disable-next-line
  }, [courseId, selectedSubject, debouncedSearchTerm]);

  const handlePromptClear = () => {
    setThePrompt("");
    _setExamList(liveExams);
  };

  const handleSearch = (val) => {
    setSearchText(val ?? "");
    if (!val || val?.length < 1) {
      handlePromptClear();
    }
  };

  const handleSearchSubmit = () => {
    const modifiedExams = liveExams?.filter((item) =>
      item?.examId?.title?.toLowerCase().includes(searchText?.toLowerCase() ?? "")
    );
    _setExamList(modifiedExams);
  };
  useEffect(
    handleSearchSubmit,
    // eslint-disable-next-line
    [debouncedSearchTerm]
  );

  const memoizedExamList = useMemo(
    () => {
      const now = new Date();
      return _examList.filter((exam) => {
        if (selectedSubject && selectedSubject !== exam?.examId?.subjectId) {
          return false; // 1. Filter by subject first (if a subject is selected).
        }

        const endsAt = moment(exam?.endsAt); // 2. Filter by the selected exam list type (Previous vs. Upcoming).
        if (examList === "PreviousExams") {
          return endsAt.isSameOrBefore(now);
        }
        if (examList === "UpcomingExams") {
          return endsAt.isSameOrAfter(now);
        }

        return false; // If examList is neither, don't show anything (or adjust as needed).
      });
    },
    // eslint-disable-next-line
    [_examList, selectedSubject, examList, allSubjects]
  );

  return (
    <React.Fragment>
      <Row
        className="selectLiveExamType"
        style={{ marginRight: 0, marginLeft: 0 }}
      >
        <Col className="exam-filter-nav" xs={24} md={24}>
          <div
            className="selectSubject-select"
            style={{ display: "flex", alignItems: "center" }}
          >
            <Select
              value={selectedSubject}
              allowClear
              onChange={props.changedSelectedSubject}
              placeholder="All Subjects"
            >
              {allSubjects.map((item, index) => {
                if (
                  liveExams
                    .map((exam) => exam?.examId?.subjectId)
                    .includes(item?._id) === false
                ) {
                  return null;
                }
                return (
                  <Option key={index} value={item?._id}>
                    {item?.name}
                  </Option>
                );
              })}
            </Select>
          </div>
          <div
            className="selectLiveExamType-select"
            style={{ display: "flex", alignItems: "center" }}
          >
            <Select
              defaultValue={examList}
              value={examList}
              onChange={props.changedSelect}
            >
              <Option value="PreviousExams">Previous Exams</Option>
              <Option value="UpcomingExams">Upcoming Exams</Option>
            </Select>
          </div>
        </Col>
      </Row>
      {memoizedExamList?.length < 1 && !searchText ? (
        <></>
      ) : (
        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "right",
            padding: "12px",
            alignItems: "center",
          }}
        >
          <div style={{ position: "relative", display: "inline-block" }}>
            <Input
              ref={inputRef}
              value={searchText}
              size="large"
              style={{
                ...sharedInputStyles, // Apply the shared styles
                width: "100%",
                position: "relative",
                background: "transparent", // Keep input background transparent to see the ghost text
                color: "black", // This is the color of the text you type
                zIndex: 2, // Ensure the input text and cursor are on top
              }}
              className="transparent-input"
              placeholder="Search"
              onKeyDown={(e) => {
                if (e.key === "Tab" && thePrompt) {
                  e.preventDefault(); // stop default tabbing away
                  setSearchText(thePrompt); // fill input with prompt
                }
              }}
              onChange={(e) => handleSearch(e.target.value)}
              suffix={
                <span
                  style={{
                    border: "1px solid gray",
                    padding: "0 8px",
                    cursor: "pointer",
                    borderRadius: 90,
                  }}
                  onClick={() => {
                    handleSearch("");
                    handlePromptClear();
                  }}
                >
                  x
                </span>
              }
            />

            {/* Inline ghost text */}
            {thePrompt && searchText && (
              <div
                style={{
                  ...sharedInputStyles, // Apply the exact same shared styles
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  color: "transparent", // This part hides the text that the user already typed
                  pointerEvents: "none", // Allows clicks to pass through to the input below
                  display: "flex",
                  alignItems: "center",
                  zIndex: 1, // Place it behind the input's text
                }}
              >
                <span style={{ paddingLeft: "12px" }}>{searchText}</span>
                <span style={{ color: "rgba(0, 0, 0, 0.35)" }}>
                  {thePrompt.slice(searchText.length)}
                </span>
              </div>
            )}
          </div>

          <Button
            size="large"
            type="primary"
            style={{ borderRadius: 10 }}
            onClick={handleSearchSubmit}
          >
            Search
          </Button>
        </div>
      )}

      <Row
        gutter={[16, 16]}
        style={{ marginRight: 0, marginLeft: 0, paddingBottom: 50 }}
      >
        {memoizedExamList.map((exam, index) => (
          <Col key={index} xs={24} sm={12} md={6} lg={6}>
            <ExamCard exam={exam} allSubjects={allSubjects} />
          </Col>
        ))}
      </Row>
    </React.Fragment>
  );
};

export default LiveExamTab;
